import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Mail, ChevronRight, Save } from 'lucide-react';
import axios from 'axios';
import PremiumLock from '@/components/PremiumLock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LettersPage() {
  const navigate = useNavigate();
  const { hasAccess, getAuthHeader } = useAuth();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [letterContent, setLetterContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (hasAccess) {
      fetchTemplates();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/letters`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Kunde inte hämta innehåll');
    } finally {
      setLoading(false);
    }
  };

  const saveToJournal = async () => {
    if (!letterContent.trim()) return;
    setSaving(true);
    try {
      const content = `## ${selectedTemplate.title}\n\n${selectedTemplate.recipient},\n\n${letterContent}\n\n---\n*${selectedTemplate.closing_reflection}*`;
      await axios.post(`${API}/journal`, {
        content,
        tags: ['brev', selectedTemplate.id]
      }, { headers: getAuthHeader() });
      toast.success('Brevet sparat i din dagbok');
      setSelectedTemplate(null);
      setLetterContent('');
    } catch (error) {
      toast.error('Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full hover:bg-secondary/80">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Startsida
            </Button>
            <h1 className="font-heading text-xl font-light">Brev till dig själv</h1>
            <div className="w-24" />
          </div>
        </header>
        <PremiumLock feature="Brev till dig själv" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="letters-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => selectedTemplate ? setSelectedTemplate(null) : navigate('/category/thoughts')}
            className="rounded-full hover:bg-secondary/80"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {selectedTemplate ? 'Tillbaka' : 'Startsida'}
          </Button>
          <h1 className="font-heading text-xl font-light">Brev till dig själv</h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
          </div>
        ) : selectedTemplate ? (
          <div className="animate-fade-in space-y-8">
            <div className="bg-violet-100/50 rounded-2xl p-8">
              <Mail className="w-10 h-10 text-primary mb-4" />
              <h2 className="font-heading text-3xl font-light mb-2">{selectedTemplate.title}</h2>
              <p className="text-muted-foreground">{selectedTemplate.description}</p>
            </div>

            <div className="bg-secondary/30 rounded-2xl p-6 space-y-3">
              <p className="text-sm text-muted-foreground">Tänk på dessa frågor medan du skriver:</p>
              <ul className="space-y-2">
                {selectedTemplate.prompts.map((prompt, i) => (
                  <li key={i} className="flex gap-3 items-start text-sm">
                    <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-xs text-primary shrink-0">{i + 1}</span>
                    <span className="text-muted-foreground">{prompt}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-card rounded-2xl p-8 space-y-4">
              <p className="font-heading text-lg text-muted-foreground">{selectedTemplate.recipient},</p>
              <Textarea
                value={letterContent}
                onChange={(e) => setLetterContent(e.target.value)}
                placeholder="Skriv ditt brev här..."
                className="min-h-[300px] rounded-xl bg-secondary/30 border-0 focus-visible:ring-1 focus-visible:ring-primary resize-none"
              />
            </div>

            <div className="bg-primary/5 rounded-xl p-6 text-center">
              <p className="text-muted-foreground italic">{selectedTemplate.closing_reflection}</p>
            </div>

            <div className="flex justify-center">
              <Button onClick={saveToJournal} disabled={!letterContent.trim() || saving} className="rounded-full px-8">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Spara i dagboken
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-center mb-10 space-y-3 animate-fade-in">
              <div className="w-16 h-16 mx-auto rounded-full bg-violet-100/50 flex items-center justify-center">
                <Mail className="w-7 h-7 text-primary" />
              </div>
              <h2 className="font-heading text-3xl font-light">Brev till dig själv</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Skriv brev till ditt yngre jag, framtida jag, eller någon du behöver kommunicera med.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map((template, index) => (
                <button
                  key={template.id}
                  onClick={() => { setSelectedTemplate(template); setLetterContent(''); }}
                  className="bg-violet-100/50 p-6 rounded-2xl text-left hover:shadow-soft transition-shadow opacity-0 animate-slide-up group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <Mail className="w-8 h-8 text-primary mb-3" />
                      <h3 className="font-heading text-lg">{template.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-sm text-muted-foreground italic">"Att skriva är att hela."</p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
